import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { ServiceResponse } from "@common/data-models/service/service-response";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ApiServiceCollectionRecord } from '@common/data-models/service/api-service-collection-record';
import { DataStore } from "@common/data-models/data-store/data-store";
import { RecordFactory } from "@common/data-models/records/record-factory";
import { Util } from "@common/utils/util";
import { ObjectProperties } from "@common/data-models/service/object-properties";

import { TopicRecord } from "@playbook/data-models/records/topic-record";
import { AssignmentRecord } from "@playbook/data-models/records/assignment-record";
import { TopicTemplateRecord } from "@playbook/data-models/records/topic-template-record";
import { ObjectTypes } from "@common/enums/object-types";
import { ServiceRecord } from '@suite/common/data-models/service/service-record';
import { ContactRecord } from '@suite/common/data-models/records/contact-record';

@Injectable({ providedIn: 'root' })
export class TopicService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson
    ) {
    }

    public async getTopicContent(database: string, path: string, guid: string, referal: string): Promise<ServiceResponse<TopicRecord>> { // get topic
        path = decodeURIComponent(path)
        let response: HttpResponse<any> = null;
        // let searchParams = this.nomWebServicesJson.searchParams({
        //     database: database,
        //     path: path,
        //     guid: guid
        // });
        try {
            response = await this.nomWebServicesJson.get(
                "/api/TopicContent",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        path: path,
                        guid: guid
                    },
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to get topic content",
                detailMessage: `Unable to get topic content. \n\rStackTrace: TopicService.getTopicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<TopicRecord>(false, "Unable to get topic content " + error.toString());
        }
        let serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();
        let converted = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<TopicRecord>(serviceRecord);
        await DataStore.setCollection(converted, true);
        const topic = DataStore.byGuid<TopicRecord>(converted.guid, referal);
        topic.links = [];
        for (let linkedRecord of serviceRecord.LinkedRecords) {
            if (linkedRecord.ListData.Type == ObjectTypes.CONTACT) {
                let serviceRecord: ServiceRecord = ServiceRecord.instance(linkedRecord);
                let contactRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<ContactRecord>(serviceRecord);
                DataStore.set(contactRecord);
                topic.links.push(contactRecord)
            }
        }
        return new ServiceResponse<TopicRecord>(true, DataStore.byGuid<TopicRecord>(converted.guid, referal));
    }

    public async createTopicContent(database: string, path: string, copyFromId: string, copyFromPath: string, linkToParent: boolean, fields: { [id: string]: any }, children: Array<{ [id: string]: any }>, referal: string, title?: string, additionalParams?: { [id: string]: any }): Promise<ServiceResponse<TopicRecord>> { // create topic
        let childrenToSend: Array<any> = new Array<any>();
        let response: HttpResponse<any> = null;
        if (fields) {
            Util.escapeSpecialCharsForFileName(fields)
            fields["CreatedBy"] = "CreatedBy." + this.nomWebServicesJson.UserId
        }
        try {
            if (children && children.length > 0) {
                for (let i = 0; i < children.length; i++) {
                    Util.escapeSpecialCharsForFileName(children[i])
                    children[i]["CreatedBy"] = "CreatedBy." + this.nomWebServicesJson.UserId
                    childrenToSend.push(this.nomWebServicesJson.Convertor.toENPSFields(children[i]));
                }
            }
            let requestParams = {
                database: database,
                path: path,
                dateTime: "",
                user: this.nomWebServicesJson.UserId,
                fieldsToUpdate: this.nomWebServicesJson.Convertor.toENPSFields(fields),
                children: childrenToSend,
                templateGuid: copyFromId,
                templateProgramId: copyFromPath,
                linkToParent: linkToParent,
                wireDatabase: (additionalParams) ? additionalParams.wireDatabase : null,
                wirePath: (additionalParams) ? additionalParams.wirePath : null,
                wireGuid: (additionalParams) ? additionalParams.wireGuid : null,
                title: title
            }
            response = await this.nomWebServicesJson.put(
                "/api/TopicContent",
                requestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create topic",
                detailMessage: `Unable to create topic. \n\rStackTrace: TopicService.createTopicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<TopicRecord>(false, "Unable to create topic " + error.toString());
        }
        let serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();

        if (path.endsWith("\\L")) { //template
            let topicTemplateRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionTemplateRecord<TopicTemplateRecord>(serviceRecord);
            return new ServiceResponse<TopicTemplateRecord>(true, topicTemplateRecord);
        } else {
            let topicRecord = this.nomWebServicesJson.Convertor.fromServiceCollectionRecord<TopicRecord>(serviceRecord);
            await DataStore.setCollection(topicRecord, true);
            const topic = DataStore.byGuid<TopicRecord>(topicRecord.guid, referal);
            topic.links = [];
            for (let linkedRecord of serviceRecord.LinkedRecords) {
                if (linkedRecord.ListData.Type == ObjectTypes.CONTACT) {
                    let contactServiceRecord: ServiceRecord = ServiceRecord.instance(linkedRecord);
                    let contactRecord = this.nomWebServicesJson.Convertor.fromServiceRecord<ContactRecord>(contactServiceRecord);
                    DataStore.set(contactRecord);
                    topic.links.push(contactRecord)
                }
            }
            return new ServiceResponse<TopicRecord>(true, DataStore.byGuid<TopicRecord>(topicRecord.guid, referal));
        }
    }

    public async getTopicTemplateContent(database: string, path: string, guid: string, referal: string): Promise<ServiceResponse<TopicTemplateRecord>> {
        let response: HttpResponse<any> = null;
        // let searchParams = this.nomWebServicesJson.searchParams({
        //     database: database,
        //     path: path,
        //     guid: guid
        // });
        try {
            response = await this.nomWebServicesJson.get(
                "/api/TopicContent",
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    params: {
                        database: database,
                        path: path,
                        guid: guid
                    },
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to get topic content"
            });
            return new ServiceResponse<TopicTemplateRecord>(false, "Unable to get topic content " + error.toString());
        }
        let serviceRecord = ServiceCollectionRecord.instance(response.body);
        serviceRecord.instantiateChildren();
        let converted = this.nomWebServicesJson.Convertor.fromServiceCollectionTemplateRecord<TopicTemplateRecord>(serviceRecord);
        return new ServiceResponse<TopicTemplateRecord>(true, converted);
    }

    public async insertIntoTopicContent(database: string, collectionPath: string, collectionGuid: string, insertBeforeGuid: string, fields: { [id: string]: any }, returnText: boolean, returnTextLevel: string, referal: string, linkToParent: boolean): Promise<ServiceResponse<AssignmentRecord>> { // insert assignment
        let response: HttpResponse<any> = null;
        Util.escapeSpecialCharsForFileName(fields)
        let requestParams = {
            database: database,
            collectionPath: collectionPath,
            collectionGuid: collectionGuid,
            insertBeforeGuid: insertBeforeGuid,
            fieldsToUpdate: this.nomWebServicesJson.Convertor.toENPSFields(fields),
            returnText: returnText,
            returnTextLevel: returnTextLevel,
            linkToParent: linkToParent
        }
        try {
            response = await this.nomWebServicesJson.patch(
                "/api/TopicContent",
                requestParams,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        "X-ENPS-TOKEN": this.nomWebServicesJson.NomTokenId
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Unable to create assignment",
                detailMessage: `Unable to create assignment. \n\rStackTrace: TopicService.insertIntoTopicContent(); \n\rError: ${error}`
            });
            return new ServiceResponse<AssignmentRecord>(false, "Unable to create assignment " + error.toString());
        }
        if (response.body.hasOwnProperty('Stories')) {
            let objectProperties = new ObjectProperties(response.body.Stories[0]);
            let record = RecordFactory.createRecordForType(fields.Type);
            this.nomWebServicesJson.Convertor.deserialize(record, objectProperties);
            await DataStore.set(record);
            return new ServiceResponse<AssignmentRecord>(true, DataStore.byGuid<AssignmentRecord>(record.guid, referal));
        }
        return new ServiceResponse<AssignmentRecord>(false, "Unexpected response in create assignment call" + response.body.toString());
    }

}
